/* eslint-disable react-hooks/rules-of-hooks */
'use client';

import { ISelectType } from '@/components/atoms/select';
import { UserInfo } from '@/interfaces/home';
import { ChartTopTagDetail } from '@/interfaces/indicator';
import { useSession } from 'next-auth/react';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { setUserData } from '../modules/user';
import { decodeData, encodeData } from '@/services/apis/http-server';

interface DataProviderProps {
  children?: React.ReactNode;
}

interface IDataType {
  isLogin: boolean;
  addons: string[];
  innerWidth: number;
  dataQuestion: string;
  plans: ISelectType[];
  indicatorSummary: any;
  isPauseAutoPlay: boolean;
  userInfo: UserInfo | undefined;
  navIndicator: ChartTopTagDetail[];
  statusAssitant: string | undefined;
  setPlans: (plans: ISelectType[]) => void;
  setUserInfo: (userInfo: UserInfo) => void;
  handleSetAutoPlay: (value: boolean) => void;
  handleSetDataQuestion: (value: string) => void;
  handleSetStatusAssitant: (value: string | undefined) => void;
  setIndicatorSummary: React.Dispatch<React.SetStateAction<any>>;
}

const DataContext = createContext<IDataType>({} as IDataType);

const DataProvider: React.FC<DataProviderProps> = ({ children }) => {
  const dispatch = useDispatch();
  const { data: session } = useSession();

  // States
  const [innerWidth, setInnerWidth] = useState(0);
  const [addons, setAddons] = useState<string[]>([]);
  const [plans, setPlans] = useState<ISelectType[]>([]);
  const [isLogin, setIsLogin] = useState<boolean>(false);
  const [dataQuestion, setDataQuestion] = useState<string>('');
  const [indicatorSummary, setIndicatorSummary] = useState(null);
  const [statusAssitant, setStatusAsssitant] = useState<string>();
  const [userInfo, setUserInfo] = useState<UserInfo>({} as UserInfo);
  const [isPauseAutoPlay, setIsPauseAutoPlay] = useState<boolean>(false);
  const [navIndicator, setNavIndicator] = useState<ChartTopTagDetail[]>([]);

  useEffect(() => {
    const handleSession = async () => {
      if (!session?.info) {
        setIsLogin(false);
        return;
      }

      try {
        const decode: any = await decodeData(session.info);
        if (decode) {
          const { accessToken, refreshToken, profile, accessTokenExpires } =
            decode;

          dispatch(
            setUserData({
              access_token: accessToken,
              refresh_token: refreshToken,
              user: profile as any,
            }),
          );

          const encodedAccessToken = await encodeData(
            JSON.stringify({ accessToken }),
          );
          localStorage.setItem('accessToken', encodedAccessToken);
          setUserInfo(profile as any);
          setIsLogin(true);
        }
      } catch (error) {
        console.error('Error decoding session data:', error);
        setIsLogin(false);
      }
    };

    handleSession();
  }, [session, dispatch]);

  // Window resize effect
  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      const width = window.innerWidth;
      setInnerWidth(width);
    };

    setInnerWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useLayoutEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUserInfo(JSON.parse(storedUser));
    }
  }, []);

  useEffect(() => {
    if (!isPauseAutoPlay) return;
    const timer = setTimeout(() => setIsPauseAutoPlay(false), 120000);
    return () => clearTimeout(timer);
  }, [isPauseAutoPlay]);

  const handleSetStatusAssitant = useCallback((value: string | undefined) => {
    setStatusAsssitant(value);
  }, []);

  const handleSetDataQuestion = useCallback((value: string) => {
    setDataQuestion(value);
  }, []);

  const handleSetAutoPlay = useCallback((value: boolean) => {
    setIsPauseAutoPlay(value);
  }, []);

  // Memoized context value
  const contextValue = useMemo(
    () => ({
      plans,
      addons,
      isLogin,
      userInfo,
      innerWidth,
      dataQuestion,
      navIndicator,
      statusAssitant,
      isPauseAutoPlay,
      indicatorSummary,
      setPlans,
      setUserInfo,
      handleSetAutoPlay,
      setIndicatorSummary,
      handleSetDataQuestion,
      handleSetStatusAssitant,
    }),
    [
      plans,
      addons,
      isLogin,
      userInfo,
      innerWidth,
      dataQuestion,
      navIndicator,
      statusAssitant,
      isPauseAutoPlay,
      indicatorSummary,
    ],
  );

  return (
    <DataContext.Provider value={contextValue}>{children}</DataContext.Provider>
  );
};

function useData(): IDataType {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
}

export { DataProvider, useData };
